import { useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import nhsProvidingServices from "@/assets/nhs-providing-services.webp";
import nhsLogo from "@/assets/nhs-logo.webp";

const NhsPill = ({ align = "center" }: { align?: "center" | "left" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY < window.innerHeight * 0.2);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (!isVisible) return null;

  return (
    <div className={`w-full flex px-4 transition-all duration-300 ${align === "left" ? "justify-start px-0" : "justify-center"}`}>
      <div 
        className={`bg-mist/95 backdrop-blur-sm shadow-sm transition-all duration-500 ease-out ${
          isOpen 
            ? 'rounded-2xl shadow-lg w-[340px] max-w-[90vw]' 
            : `rounded-xl ${align === 'left' ? 'w-[300px]' : ''}`
        }`}
      >
        {/* Header - always visible */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 transition-all duration-300 hover:bg-mist ${
            isOpen ? 'border-b border-mist-foreground/10 rounded-t-2xl' : 'rounded-xl'
          }`}
        >
          <img
            src={nhsLogo}
            alt="NHS logo"
            className="w-7 h-auto"
          />
          <span className="text-tiny font-medium text-mist-foreground">
            Pharmacy Contraception Service
          </span>
          <ChevronDown
            className={`w-3.5 h-3.5 text-mist-foreground transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
          />
        </button>

        {/* Expanded content */}
        <div 
          className={`overflow-hidden transition-all duration-500 ease-out ${
            isOpen ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
        <div className="p-4 pt-3">
            {/* Logos row - at top */}
            <div className="flex items-center justify-center gap-3 mb-3 pb-3 border-b border-mist-foreground/10">
              {/* Pharmacy Services Direct - grey link */}
              <a 
                href="https://imaanhealthcare.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-0.5 hover:opacity-80 transition-opacity"
              >
                <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.1em] whitespace-nowrap flex items-center justify-center gap-0.5">Pharmacy Services Direct <span className="text-[13px] leading-none -mt-[2px] -ml-0.5" style={{ fontWeight: 950 }}>+</span></span>
              </a>
              <div className="w-px h-10 bg-mist-foreground/15" />
              {/* NHS Providing NHS Services logo - official format */}
              <div className="flex flex-col items-center gap-0.5">
                <img src={nhsProvidingServices} alt="Providing NHS services" className="w-32 h-auto" />
              </div>
            </div>

            <p className="text-tiny text-navy/80 leading-relaxed text-center mb-3">
              Contraception Direct is provided by Pharmacy Services Direct in St. Helens, England under the NHS Pharmacy Contraception Service. This is a national programme that allows pharmacies to supply contraceptive pills for free, without a prescription.
            </p>
            <p className="text-tiny text-navy/60 leading-relaxed text-center mb-4">
              All consultations are carried out by GPhC registered pharmacists. Your contraception is dispensed from our regulated pharmacy and posted directly to you at no cost.
            </p>

            <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-tiny">
              <a
                href="https://imaanhealthcare.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-mist-foreground hover:underline font-medium inline-flex items-center gap-1"
              >
                Pharmacy Services Direct
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
              <a
                href="https://www.england.nhs.uk/primary-care/pharmacy/pharmacy-services/nhs-pharmacy-contraception-service/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-mist-foreground hover:underline font-medium inline-flex items-center gap-1"
              >
                Learn more
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NhsPill;
